export const E_BillingCatalogSource = {
  STATIC: "static",
  FALLBACK: "fallback",
} as const;

export type T_BillingCatalogSource =
  (typeof E_BillingCatalogSource)[keyof typeof E_BillingCatalogSource];

export const E_BillingOfferingKey = {
  STANDARD: "standard",
} as const;

export type T_BillingOfferingKey =
  (typeof E_BillingOfferingKey)[keyof typeof E_BillingOfferingKey];

export const E_BillingPlanKey = {
  FREE: "FREE",
  PRO: "PRO",
  MAX: "MAX",
} as const;

export type T_BillingPlanKey =
  (typeof E_BillingPlanKey)[keyof typeof E_BillingPlanKey];

export type T_BillingPaidPlanKey = Exclude<T_BillingPlanKey, "FREE">;

export const E_BillingPeriod = {
  MONTHLY: "MONTHLY",
  ANNUAL: "ANNUAL",
} as const;

export type T_BillingPeriod =
  (typeof E_BillingPeriod)[keyof typeof E_BillingPeriod];

export const E_BillingPurchaseIntent = {
  PRO_MONTHLY: "PRO_MONTHLY",
  PRO_ANNUAL: "PRO_ANNUAL",
  MAX_MONTHLY: "MAX_MONTHLY",
  MAX_ANNUAL: "MAX_ANNUAL",
} as const;

export type T_BillingPurchaseIntent =
  (typeof E_BillingPurchaseIntent)[keyof typeof E_BillingPurchaseIntent];

export const E_BillingPlanFeatureState = {
  INCLUDED: "included",
  LOCKED: "locked",
} as const;

export type T_BillingPlanFeatureState =
  (typeof E_BillingPlanFeatureState)[keyof typeof E_BillingPlanFeatureState];

export const E_BillingCatalogSurface = {
  MOBILE_PAYWALL: "mobile_paywall",
  WEB_PRICING: "web_pricing",
  CHAT_LIMIT_BANNER: "chat_limit_banner",
  TRIAL_FOOTER: "trial_footer",
} as const;

export type T_BillingCatalogSurface =
  (typeof E_BillingCatalogSurface)[keyof typeof E_BillingCatalogSurface];

export const E_BillingClientPlatform = {
  IOS: "ios",
  ANDROID: "android",
  WEB: "web",
} as const;

export type T_BillingClientPlatform =
  (typeof E_BillingClientPlatform)[keyof typeof E_BillingClientPlatform];

export const BILLING_PLAN_ORDER = [
  E_BillingPlanKey.FREE,
  E_BillingPlanKey.PRO,
  E_BillingPlanKey.MAX,
] as const;

export const BILLING_PAID_PLAN_KEYS = [
  E_BillingPlanKey.PRO,
  E_BillingPlanKey.MAX,
] as const;

export const BILLING_PURCHASE_INTENTS_BY_PLAN_PERIOD = {
  PRO: {
    MONTHLY: E_BillingPurchaseIntent.PRO_MONTHLY,
    ANNUAL: E_BillingPurchaseIntent.PRO_ANNUAL,
  },
  MAX: {
    MONTHLY: E_BillingPurchaseIntent.MAX_MONTHLY,
    ANNUAL: E_BillingPurchaseIntent.MAX_ANNUAL,
  },
} as const;

export type T_BillingPlanFeature = {
  label: string;
  state: T_BillingPlanFeatureState;
};

export type T_BillingOffering = {
  key: string;
  revenuecat_offering_id: string;
  campaign_key: string | null;
  display_name: string;
  hero_title: string;
  hero_title_accent: string | null;
  hero_body: string;
  promo_badge: string | null;
  default_billing_period: T_BillingPeriod;
  starts_at: string | null;
  ends_at: string | null;
};

export type T_BillingPlan = {
  plan_key: T_BillingPlanKey;
  label: string;
  blurb: string;
  neutral_tag: string;
  selected_tag: string;
  cta_label: string | null;
  cta_note: string | null;
  features: T_BillingPlanFeature[];
  sort_order: number;
};

export type T_BillingPurchaseOption = {
  purchase_intent: T_BillingPurchaseIntent;
  plan_key: T_BillingPaidPlanKey;
  billing_period: T_BillingPeriod;
  revenuecat_package_id: string;
  is_default: boolean;
  promo_label: string | null;
  legal_note: string | null;
  display_price: {
    currency_code: string;
    intro_period_label: "first month" | "first year" | null;
    intro_price_amount: number | null;
    intro_price_string: string | null;
    intro_savings_pct: number | null;
    regular_price_amount: number;
    regular_price_string: string;
  } | null;
  sort_order: number;
};

export type T_BillingCatalog = {
  source: T_BillingCatalogSource;
  selected_for_user: string;
  offering: T_BillingOffering;
  plans: T_BillingPlan[];
  purchase_options: T_BillingPurchaseOption[];
  generated_at: string;
};
